/**
 * Super-admin · Email Analytics dashboard.
 *
 * Reads:
 *   GET /api/admin/emails/summary?days=30
 *   GET /api/admin/emails?limit=&offset=&tag=&sender=&status=&q=
 *   GET /api/admin/emails/{id}/events
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, Send, Eye, MousePointerClick, AlertTriangle, RefreshCw, Search } from "lucide-react";
import api from "../../lib/api";

const cad = (n) => new Intl.NumberFormat("en-CA").format(Number(n || 0));
const pct = (num, den) => (!den ? "—" : `${((num / den) * 100).toFixed(1)}%`);

function Tile({ icon: Icon, label, value, sub, tone = "slate" }) {
  const toneClass = {
    slate:  "bg-slate-50 border-slate-200 text-slate-700",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:  "bg-amber-50 border-amber-200 text-amber-700",
    red:    "bg-red-50 border-red-200 text-red-700",
    cyan:   "bg-cyan-50 border-cyan-200 text-cyan-700",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1 font-display text-3xl">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
    </div>
  );
}


export default function EmailAnalytics() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [tag, setTag] = useState("");
  const [sender, setSender] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([
      api.get(`/admin/emails/summary?days=${days}`),
      api.get(`/admin/emails`, { params: { limit: 25, offset, tag, sender, status, q } }),
    ]);
    setSummary(s.data);
    setRows(l.data.rows || []);
    setTotal(l.data.total || 0);
  }, [days, offset, tag, sender, status, q]);

  useEffect(() => { load(); }, [load]);

  const openTimeline = async (row) => {
    setSelected(row); setEvents([]);
    const { data } = await api.get(`/admin/emails/${row.id}/events`);
    setEvents(data.events || []);
  };

  const t = summary?.totals || {};
  return (
    <div className="space-y-6" data-testid="admin-email-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Email analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Every send, tagged by purpose. Live Resend delivery events roll up onto each row.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-slate-200 rounded-md px-2 py-1.5" data-testid="email-window-select">
            {[7, 14, 30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{`Last ${d} days`}</option>)}
          </select>
          <button onClick={load} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border rounded-md hover:bg-slate-50" data-testid="email-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="email-summary-tiles">
        <Tile icon={Send}                 tone="cyan"  label="Sent"       value={cad(t.sent)}       sub={`across ${summary?.by_tag?.length || 0} tag${(summary?.by_tag?.length||0)===1?"":"s"}`} />
        <Tile icon={Mail}                 tone="green" label="Delivered"  value={cad(t.delivered)}  sub={pct(t.delivered, t.sent) + " of sends"} />
        <Tile icon={Eye}                  tone="slate" label="Opens"      value={cad(t.opens)}      sub={pct(t.opens, t.delivered) + " open rate"} />
        <Tile icon={MousePointerClick}    tone="slate" label="Clicks"     value={cad(t.clicks)}     sub={pct(t.clicks, t.opens) + " CTR"} />
        <Tile icon={AlertTriangle}        tone="red"   label="Issues"     value={cad((t.bounced||0)+(t.complained||0)+(t.blocked||0))} sub={`${cad(t.bounced)} bounced · ${cad(t.blocked)} blocked`} />
      </div>

      {/* Breakdown tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 min-w-0 overflow-x-auto">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">By tag</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="text-left py-1">Tag</th><th className="text-right">Sent</th><th className="text-right">Deliv.</th><th className="text-right">Opens</th><th className="text-right">Clicks</th></tr>
            </thead>
            <tbody>
              {(summary?.by_tag || []).map(r => (
                <tr key={r.tag} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => { setTag(r.tag === "(untagged)" ? "" : r.tag); setOffset(0); }}>
                  <td className="py-1.5 font-mono text-[11px] text-slate-700 truncate max-w-[240px]">{r.tag}</td>
                  <td className="text-right">{cad(r.count)}</td>
                  <td className="text-right">{cad(r.delivered)}</td>
                  <td className="text-right">{cad(r.opens)}</td>
                  <td className="text-right">{cad(r.clicks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 min-w-0 overflow-x-auto">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">By sender</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="text-left py-1">Sender</th><th className="text-right">Sent</th><th className="text-right">Deliv.</th><th className="text-right">Opens</th><th className="text-right">Clicks</th></tr>
            </thead>
            <tbody>
              {(summary?.by_sender || []).map(r => (
                <tr key={r.sender} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => { setSender(r.sender === "(legacy)" ? "" : r.sender); setOffset(0); }}>
                  <td className="py-1.5 font-mono text-[11px] text-slate-700">{r.sender}@…</td>
                  <td className="text-right">{cad(r.count)}</td>
                  <td className="text-right">{cad(r.delivered)}</td>
                  <td className="text-right">{cad(r.opens)}</td>
                  <td className="text-right">{cad(r.clicks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-2" data-testid="email-filters">
        <div className="inline-flex items-center gap-1 px-2 py-1.5 border border-slate-200 rounded-md">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input value={q} onChange={(e)=>{setQ(e.target.value); setOffset(0);}} placeholder="Search recipient / subject…"
                 className="text-sm outline-none w-64" data-testid="email-search"/>
        </div>
        {tag && <button onClick={()=>{setTag(""); setOffset(0);}} className="text-xs px-2 py-1 bg-cyan-50 border border-cyan-200 rounded-md text-cyan-800">tag: {tag} ✕</button>}
        {sender && <button onClick={()=>{setSender(""); setOffset(0);}} className="text-xs px-2 py-1 bg-cyan-50 border border-cyan-200 rounded-md text-cyan-800">sender: {sender} ✕</button>}
        <select value={status} onChange={(e)=>{setStatus(e.target.value); setOffset(0);}} className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">Any status</option>
          <option value="sent">sent</option>
          <option value="delivered">delivered</option>
          <option value="delayed">delayed</option>
          <option value="bounced">bounced</option>
          <option value="complained">complained</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{cad(total)} results</span>
      </div>

      {/* Rows */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 min-w-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-slate-400">
            <tr>
              <th className="text-left py-1.5">Sent</th>
              <th className="text-left">Recipient</th>
              <th className="text-left">Subject</th>
              <th className="text-left">Tag</th>
              <th className="text-left">Sender</th>
              <th className="text-left">Status</th>
              <th className="text-right">Opens</th>
              <th className="text-right">Clicks</th>
            </tr>
          </thead>
          <tbody data-testid="email-rows">
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={()=>openTimeline(r)} data-testid={`email-row-${r.id}`}>
                <td className="py-2 text-xs text-slate-500">{(r.sent_at||"").slice(0,19).replace("T"," ")}</td>
                <td className="text-slate-700 truncate max-w-[180px]">{r.to}</td>
                <td className="text-slate-700 truncate max-w-[260px]">{r.subject}</td>
                <td className="font-mono text-[10px] text-slate-500">{r.tag || "—"}</td>
                <td className="font-mono text-[10px] text-slate-500">{r.sender_alias || "—"}</td>
                <td>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    r.delivery_status === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : r.delivery_status === "bounced" ? "bg-red-50 text-red-700 border-red-200"
                    : r.delivery_status === "sent"    ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                  }`}>{r.delivery_status || r.provider || "?"}</span>
                </td>
                <td className="text-right text-slate-700">{cad(r.opens || 0)}</td>
                <td className="text-right text-slate-700">{cad(r.clicks || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-3 text-xs">
          <button disabled={offset === 0} onClick={()=>setOffset(Math.max(0, offset-25))}
                  className="px-2 py-1 border rounded-md disabled:opacity-40">← Prev</button>
          <div className="text-slate-500">Rows {offset+1}–{Math.min(offset+25, total)} of {cad(total)}</div>
          <button disabled={offset+25 >= total} onClick={()=>setOffset(offset+25)}
                  className="px-2 py-1 border rounded-md disabled:opacity-40">Next →</button>
        </div>
      </div>

      {/* Timeline drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center" onClick={()=>setSelected(null)}>
          <div className="bg-white w-full md:w-[640px] max-h-[80vh] overflow-y-auto rounded-t-2xl md:rounded-2xl p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Event timeline</div>
            <div className="text-slate-900 font-semibold truncate">{selected.subject}</div>
            <div className="text-xs text-slate-500 mb-3">
              to <span className="font-mono">{selected.to}</span> ·
              tag <span className="font-mono">{selected.tag || "—"}</span> ·
              from <span className="font-mono">{selected.sender_alias || "?"}@</span>
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-slate-500 italic">
                No delivery events yet. Once your Resend webhook is live, events will stream in.
              </div>
            ) : (
              <ul className="border-l border-slate-200 pl-4 space-y-2">
                {events.map(e => (
                  <li key={e.id} className="relative">
                    <span className="absolute -left-[9px] top-2 w-2 h-2 rounded-full bg-cyan-500"></span>
                    <div className="text-xs text-slate-500">{(e.created_at||"").slice(0,19).replace("T"," ")}</div>
                    <div className="text-sm font-mono">{e.event_type}</div>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={()=>setSelected(null)} className="mt-4 text-sm px-3 py-1.5 border rounded-md">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
